import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Snackbar as MuiSnackbar, SnackbarCloseReason, useMediaQuery, useTheme } from '@mui/material';
import { Addon, ShopContent } from '../../../types/cms_shared_types';
import { ShoppingCart as ShoppingCartIcon } from 'phosphor-react';
import { Product } from '../../../types/webshop';
import { getCartItemChanges } from './utils';

export const AUTO_HIDE_DURATION = 3000;

interface SnackbarProps {
  shopContent: ShopContent;
  selectedProducts: Product[];
  selectedAddons: Addon[];
  emptyCartSilently: boolean;
  resetEmptyCartSilently: () => void;
}

export const Snackbar: React.FC<SnackbarProps> = ({
  shopContent,
  selectedProducts,
  selectedAddons,
  emptyCartSilently = false,
  resetEmptyCartSilently,
}) => {
  const [message, setMessage] = useState('');
  const [open, setOpen] = useState(false);
  const [key, setKey] = useState(0);

  const prevSelectedProductsRef = useRef<Product[]>(selectedProducts);
  const prevSelectedAddonsRef = useRef<Addon[]>(selectedAddons);

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const handleClose = (event?: React.SyntheticEvent | Event, reason?: SnackbarCloseReason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  };

  const activateSnackbar = useCallback(
    <T extends Addon | Product>(updatedItems: T[], prevItems: T[]) => {
      const { addedItems, removedItems, editedItems } = getCartItemChanges(updatedItems, prevItems);

      const noChange = [...addedItems, ...removedItems, ...editedItems].length === 0;

      if (noChange) {
        return;
      }

      const cartCleared = selectedProducts.length === 0 && selectedAddons.length === 0;

      if (emptyCartSilently && cartCleared) {
        resetEmptyCartSilently();
        return;
      }

      if (cartCleared) {
        setMessage(shopContent.shoppingCart.snackbar.cartEmptied);
      } else if (addedItems.length > 0) {
        setMessage(`${addedItems[0].name} ${shopContent.shoppingCart.snackbar.added}`);
      } else if (removedItems.length > 0) {
        setMessage(`${removedItems[0].name} ${shopContent.shoppingCart.snackbar.removed}`);
      } else if (editedItems.length > 0) {
        setMessage(`${editedItems[0].name} ${shopContent.shoppingCart.snackbar.updated}`);
      }

      setOpen(true);
      setKey((prevKey) => 1 - prevKey); // Resets autoHideDuration
    },
    [
      selectedAddons.length,
      selectedProducts.length,
      shopContent.shoppingCart.snackbar,
      emptyCartSilently,
      resetEmptyCartSilently,
    ],
  );

  useEffect(() => {
    const prevSelectedProducts = prevSelectedProductsRef.current;
    activateSnackbar(selectedProducts, prevSelectedProducts);
    prevSelectedProductsRef.current = selectedProducts;
  }, [selectedProducts, activateSnackbar]);

  useEffect(() => {
    const prevSelectedAddons = prevSelectedAddonsRef.current;
    activateSnackbar(selectedAddons, prevSelectedAddons);
    prevSelectedAddonsRef.current = selectedAddons;
  }, [selectedAddons, activateSnackbar]);

  useEffect(() => {
    if (emptyCartSilently) {
      setOpen(false);
    }
  }, [emptyCartSilently]);

  return (
    <MuiSnackbar
      open={open}
      key={key}
      autoHideDuration={AUTO_HIDE_DURATION}
      message={
        <div className="flex flex-row items-center gap-4 text-sm text-input-gray">
          <ShoppingCartIcon size={24} />
          {message}
        </div>
      }
      onClose={handleClose}
      anchorOrigin={{ horizontal: isSmallScreen ? 'center' : 'right', vertical: 'top' }}
      sx={{
        '& .MuiSnackbar-root': {
          maxWidth: 'unset',
        },
        '& .MuiPaper-root': {
          borderRadius: 4,
          maxWidth: '50vw',
          justifyContent: 'center',
        },
        '& .MuiSnackbarContent-root': {
          paddingX: 2.5,
          paddingY: 0.5,
          fontFamily: 'Montserrat',
          minWidth: 'fit-content',
        },
      }}
      className="mt-24 sm:mt-28"
      disableWindowBlurListener
    />
  );
};
